import React, { useContext, useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import Pagination from "react-js-pagination";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function PhysioPatientDetails() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    fetchRows(1, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch_id]);

  const fetchRows = async (page = 1, lim = limit, override = {}) => {
    if (!branch_id) return;
    setLoading(true);
    try {
      const res = await UserServices.getPhysioClientHub({
        branch_id,
        page,
        limit: lim,
        search: (override.search ?? search) || undefined,
        filter: (override.filter ?? filter) || "all",
      });
      if (res.status === 200 && res.data.status) {
        setRows(res.data.data || []);
        if (res.data.pagination) {
          setCurrentPage(res.data.pagination.current_page || 1);
          setTotalResults(res.data.pagination.total_record || 0);
        } else {
          setCurrentPage(1);
          setTotalResults((res.data.data || []).length);
        }
      }
    } catch (err) {
      console.log(err);
      setRows([]);
    }
    setLoading(false);
  };

  const openDetails = async (clientId) => {
    setShowModal(true);
    setDetailsLoading(true);
    setDetails(null);
    try {
      const res = await UserServices.getPhysioPatientDetails(clientId, branch_id);
      if (res.status === 200 && res.data.status) {
        setDetails(res.data.data || null);
      }
    } catch (err) {
      console.log(err);
    }
    setDetailsLoading(false);
  };

  const exportExcelLike = () => {
    const headers = [
      "Name",
      "Phone",
      "Appointments",
      "Prescriptions",
      "Referrals",
      "Responses",
      "GX",
      "Completeness %",
    ];
    const lines = rows.map((r) => [
      r.full_name || "",
      r.phone || "",
      r.counts?.appointments ?? 0,
      r.counts?.prescriptions ?? 0,
      r.counts?.referrals ?? 0,
      r.counts?.responses ?? 0,
      r.counts?.gx ?? 0,
      r.physio_completeness ?? 0,
    ]);
    const csv = [headers, ...lines]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Physio_Patient_Details_${moment().format("YYYY-MM-DD")}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(16);
    doc.text("Physio Patient Details", 14, 16);
    doc.autoTable({
      startY: 22,
      head: [["S#", "Name", "Phone", "Appointments", "Prescriptions", "Referrals", "Responses", "GX", "Completeness"]],
      body: rows.map((r, i) => [
        (currentPage - 1) * limit + i + 1,
        r.full_name || "",
        r.phone || "",
        r.counts?.appointments ?? 0,
        r.counts?.prescriptions ?? 0,
        r.counts?.referrals ?? 0,
        r.counts?.responses ?? 0,
        r.counts?.gx ?? 0,
        `${r.physio_completeness ?? 0}%`,
      ]),
      headStyles: { fillColor: [93, 64, 55], textColor: 255 },
      styles: { fontSize: 8 },
    });
    doc.save(`Physio_Patient_Details_${moment().format("YYYY-MM-DD")}.pdf`);
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="page-heading d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">
            <i className="bi bi-people-fill me-2"></i>
            Patient Details
          </h3>
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportExcelLike}>Excel</button>
            <button className="btn btn-danger" onClick={exportPdf}>PDF</button>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Search</label>
                <input
                  className="form-control"
                  placeholder="Name, UID, phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Filter</label>
                <select
                  className="form-select"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="has_any">Has Any Record</option>
                  <option value="missing_any">Missing All</option>
                  <option value="appointments">Appointments</option>
                  <option value="prescriptions">Prescriptions</option>
                  <option value="referrals">Referrals</option>
                  <option value="responses">Responses</option>
                  <option value="gx">GX</option>
                </select>
              </div>
              <div className="col-md-5 d-flex gap-2">
                <button className="btn btn-primary" onClick={() => fetchRows(1, limit)}>Search</button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setSearch("");
                    setFilter("all");
                    fetchRows(1, limit, { search: "", filter: "all" });
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4">No patient rows found.</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>S#</th>
                        <th>Patient</th>
                        <th>Phone</th>
                        <th>Appointments</th>
                        <th>Prescriptions</th>
                        <th>Referrals</th>
                        <th>Responses</th>
                        <th>GX</th>
                        <th>Completeness</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.id}>
                          <td>{(currentPage - 1) * limit + i + 1}</td>
                          <td>{r.full_name || "—"}</td>
                          <td>{r.phone || "—"}</td>
                          <td>{r.counts?.appointments ?? 0}</td>
                          <td>{r.counts?.prescriptions ?? 0}</td>
                          <td>{r.counts?.referrals ?? 0}</td>
                          <td>{r.counts?.responses ?? 0}</td>
                          <td>{r.counts?.gx ?? 0}</td>
                          <td>{r.physio_completeness ?? 0}%</td>
                          <td>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => openDetails(r.id)}>
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalResults > limit ? (
                  <div className="d-flex justify-content-center">
                    <Pagination
                      activePage={currentPage}
                      itemsCountPerPage={limit}
                      totalItemsCount={totalResults}
                      pageRangeDisplayed={5}
                      onChange={(p) => {
                        setCurrentPage(p);
                        fetchRows(p, limit);
                      }}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Patient Timeline</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailsLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : !details ? (
              <div className="text-center text-muted">No details found.</div>
            ) : (
              <>
                <div className="mb-3">
                  <h5 className="mb-1">{details.client?.name || "-"}</h5>
                  <small className="text-muted">
                    UID: {details.client?.uid || "-"} | Phone: {details.client?.phone || "-"}
                  </small>
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-md-3"><div className="border rounded p-2">Appointments: <strong>{details.summary?.appointments ?? 0}</strong></div></div>
                  <div className="col-md-3"><div className="border rounded p-2">Prescriptions: <strong>{details.summary?.prescriptions ?? 0}</strong></div></div>
                  <div className="col-md-3"><div className="border rounded p-2">Referrals: <strong>{details.summary?.referrals ?? 0}</strong></div></div>
                  <div className="col-md-3"><div className="border rounded p-2">Responses: <strong>{details.summary?.responses ?? 0}</strong></div></div>
                </div>

                <h6>Prescriptions</h6>
                <div className="table-responsive mb-3">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Diagnosis</th>
                        <th>Region</th>
                        <th>Session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(details.prescriptions || []).map((p) => (
                        <tr key={p.id}>
                          <td>{p.prescription_date ? moment(p.prescription_date).format("DD/MM/YY") : "—"}</td>
                          <td>{p.diagnosis || "—"}</td>
                          <td>{p.region || "—"}</td>
                          <td>{p.session_recommended || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h6>Appointments</h6>
                <div className="table-responsive mb-3">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Source</th>
                        <th>Slot</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(details.appointments || []).map((a) => (
                        <tr key={a.id}>
                          <td>{a.appointment_date ? moment(a.appointment_date).format("DD/MM/YY") : "—"}</td>
                          <td className="text-capitalize">{a.appointment_source || "—"}</td>
                          <td>{a.slot_start ? `${String(a.slot_start).substring(0, 5)} - ${String(a.slot_end || "").substring(0, 5)}` : "—"}</td>
                          <td>{a.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h6>Daily Referrals</h6>
                <div className="table-responsive mb-3">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Patient</th>
                        <th>Trainer</th>
                        <th>Referred</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(details.referrals || []).map((r) => (
                        <tr key={r.id}>
                          <td>{r.referral_date ? moment(r.referral_date).format("DD/MM/YY") : "—"}</td>
                          <td>{r.patient_name || "—"}</td>
                          <td>{r.trainer ? `${r.trainer.first_name || ""} ${r.trainer.last_name || ""}`.trim() : "—"}</td>
                          <td>{r.referred_count ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}
